import React from 'react';
import { ArrowUpRightIcon, CheckIcon, RotateCcwIcon, ShieldCheckIcon } from 'lucide-react';
import { pricingConfig } from '../../data/pricing';
import type { BundleId } from '../../data/pricing';
import { useFunnel } from '../../contexts/FunnelContext';
import { formatMoney, getBundle, priceBundle } from '../../utils/pricing';
import { setupFor } from '../../utils/checkout';
import { buttonClasses } from '../../utils/buttonStyles';
const m = (n: number) => formatMoney(n, 'en');
const diffOf = (from: BundleId, to: BundleId) => Math.round((priceBundle(getBundle(to)).total - priceBundle(getBundle(from)).total) * 100) / 100;

/** One strong upgrade offer (replaces the selected system), plus an optional alternative. */
export function UpgradeOfferCard() {
  const {
    state,
    upgradeTo,
    undoUpgrade
  } = useFunnel();
  if (!state) return null;
  const {
    primary,
    original
  } = state;
  const upgraded = primary.bundleId !== original.bundleId || primary.qty !== original.qty;
  if (upgraded) {
    const from = getBundle(original.bundleId);
    const to = getBundle(primary.bundleId);
    return <div className="flex items-center gap-3 border border-marine/60 bg-marine/10 px-4 py-3" data-id="element-155" data-mp="3849q4mtptvl5">
        <CheckIcon className="h-5 w-5 shrink-0 text-ice" aria-hidden data-id="element-156" data-mp="334neuixg6pez" />
        <p className="flex-1 text-sm text-white" data-id="element-157" data-mp="2z3trprrtxen5">
          Upgraded to the <span className="font-semibold" data-id="element-158" data-mp="2z5ocn633p6r8">{to.tagline}</span> — replaces your {from.tagline}.
        </p>
        <button type="button" onClick={undoUpgrade} className="inline-flex h-10 shrink-0 items-center gap-1.5 px-2 text-sm text-titanium transition-colors duration-150 hover:text-white" data-id="element-159" data-mp="3vtb2um9kzsw8">
          <RotateCcwIcon className="h-4 w-4" aria-hidden data-id="element-160" data-mp="3hubezgtw7i14" /> Undo
        </button>
      </div>;
  }

  // Multiple loose single lights: Smart System Savings handles the better configuration.
  if (primary.qty !== 1) return null;
  const offer = pricingConfig.upgradeOffers[primary.bundleId];
  if (!offer.primary) {
    return <div className="border border-white/15 bg-[#06121F] px-5 py-4" data-id="element-161" data-mp="27w1ylwh36uqs">
        <p className="font-display text-lg font-semibold uppercase tracking-[0.04em] text-white" data-id="element-162" data-mp="2cvemmkjp27uy">You chose our largest system.</p>
        <p className="mt-1 text-sm text-titanium" data-id="element-163" data-mp="1yulmnkamikkg">Lighting another area — a spa, stairs or a dock? Add another system below.</p>
      </div>;
  }
  const target = getBundle(offer.primary);
  const tp = priceBundle(target);
  const diff = diffOf(primary.bundleId, target.id);
  const setup = setupFor(target.id);
  const currentWarranty = getBundle(primary.bundleId).warranty;
  const badge = target.badge === 'mostPopular' ? 'Most popular' : target.badge === 'bestValue' ? 'Best value' : null;
  const headline = offer.headline.replace('{diff}', m(diff));
  const alt = offer.alt ? getBundle(offer.alt) : null;
  return <div className="mf-hud relative overflow-hidden border border-marine bg-[#0D2440]" data-id="element-164" data-mp="167hp20dv18ip">
      <div className="grid gap-0 sm:grid-cols-[160px_1fr]" data-id="element-165" data-mp="1b4rdpdtfdg5h">
        <div className="hidden self-start p-4 pr-0 sm:block" data-id="element-166" data-mp="rk0yn10yhlsd">
          <img src={target.image} alt={`${target.name} — ${target.qty} M1 PRO lights`} width={1122} height={1402} className="block aspect-[1122/1402] h-auto w-full object-contain" data-id="element-167" data-mp="rmdaqxk191xi" />
        </div>
        <div className="p-5 md:p-6" data-id="element-168" data-mp="wiauldui8qf1">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1" data-id="element-169" data-mp="wlgducggcy2u">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-titanium" data-id="element-170" data-mp="1ahk6i1bogyd7">Recommended upgrade</p>
            {badge && <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-ice" data-id="element-171" data-mp="15i7l7l0gfzcq">{badge}</p>}
          </div>
          <h3 className="mt-2 font-display text-2xl font-semibold uppercase leading-[1] tracking-[0.02em] text-white md:text-[28px]" data-id="element-172" data-mp="1f9vabf9jh4b1">
            {headline}.
          </h3>
          <p className="mt-2 text-[15px] text-titanium" data-id="element-173" data-mp="1t7jsgfc81xce">
            Upgrade to the <span className="text-white" data-id="element-174" data-mp="1obum9t5p5i74">{target.tagline}</span> — {target.qty} M1 PRO lights
            {setup ? <>
                {' · '}
                <span className="text-ice" data-id="element-175" data-mp="3npl3e1uw7oah">
                  {setup.installed} installed. {setup.ready} ready.
                </span>
              </> : null}
          </p>
          <div className="mt-4 flex flex-wrap items-baseline gap-x-4 gap-y-1" data-id="element-176" data-mp="cr6gfntm4a9">
            <span className="font-display text-4xl font-semibold leading-none text-white" data-id="element-177" data-mp="ev7mpuc4p6u">+{m(diff)}</span>
            <span className="text-sm text-titanium" data-id="element-178" data-mp="2zu8o6574tbns">
              New total {m(tp.total)} · {m(tp.perUnit)}/light
            </span>
          </div>
          {target.warranty === 'lifetime' && currentWarranty !== 'lifetime' && <p className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold uppercase tracking-[0.08em] text-white" data-id="element-179" data-mp="3ds6s0gnttpp1">
              <ShieldCheckIcon className="h-4 w-4 text-marine" aria-hidden data-id="element-180" data-mp="o1jwjvhrhhwy" /> Lifetime warranty included
            </p>}
          <button type="button" onClick={() => upgradeTo(target.id)} className={`${buttonClasses('primary')} mt-5 w-full`} data-id="element-181" data-mp="t3izj49lactd">
            Upgrade — +{m(diff)}
          </button>
          {alt && <button type="button" onClick={() => upgradeTo(alt.id)} className="mt-3 inline-flex min-h-[44px] w-full items-center justify-center gap-1.5 text-sm text-titanium transition-colors duration-150 hover:text-white" data-id="element-182" data-mp="y8d0ljrle5m9">
              Or the {alt.tagline} ({alt.qty} lights) for {m(priceBundle(alt).total)}
              <ArrowUpRightIcon className="h-4 w-4" aria-hidden data-id="element-183" data-mp="ff0s40yoyddt" />
            </button>}
        </div>
      </div>
    </div>;
}